/**
 * Shopify Storefront API Utilities
 * Extracted from useShopify hook for React Query migration
 */

import { productionLogger } from '@/utils/production-logger';
import { SHOPIFY_LIMITS } from '@/constants/shopify-limits';

// ============= TypeScript Interfaces =============

export interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  description: string;
  descriptionHtml?: string;
  images: ShopifyImage[];
  variants: ShopifyVariant[];
  tags: string[];
  productType: string;
  vendor: string;
  createdAt: string;
  updatedAt: string;
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  compareAtPriceRange?: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  availableForSale: boolean;
  totalInventory: number;
  options?: Array<{
    name: string;
    values: string[];
  }>;
}

export interface ShopifyImage {
  id: string;
  url: string;
  altText: string | null;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice?: {
    amount: string;
    currencyCode: string;
  };
  availableForSale: boolean;
  quantityAvailable: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  image?: ShopifyImage;
}

export interface ShopifyCollection {
  id: string;
  title: string;
  handle: string;
  description: string;
  image?: ShopifyImage;
  productHandles?: string[];
}

// ============= API Configuration =============

const SHOPIFY_STORE_DOMAIN = 'shop.bohostyleco.com';
const STOREFRONT_API_URL = `https://${SHOPIFY_STORE_DOMAIN}/api/2025-01/graphql.json`;

// SECURITY NOTE: This Shopify Storefront Access Token is intentionally public and safe to expose.
// Storefront API tokens are designed for client-side use and only provide read-only access to
// public product data. They cannot access private store information, customer data, or perform
// administrative actions. This is NOT a security risk.
const STOREFRONT_ACCESS_TOKEN = '8656d15404e1ce2d169056b4e4d1ab89';

// ============= GraphQL Queries =============

const PRODUCTS_QUERY = `
  query getProducts($first: Int = ${SHOPIFY_LIMITS.PRODUCTS_PER_PAGE}, $after: String, $sortKey: ProductSortKeys = UPDATED_AT, $reverse: Boolean = true) {
    products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          handle
          description
          descriptionHtml
          productType
          vendor
          createdAt
          updatedAt
          tags
          availableForSale
          totalInventory
          images(first: ${SHOPIFY_LIMITS.IMAGES_LIST_VIEW}) {
            edges {
              node {
                id
                url
                altText
              }
            }
          }
           variants(first: ${SHOPIFY_LIMITS.VARIANTS_PER_PRODUCT}) {
            edges {
              node {
                id
                title
                availableForSale
                quantityAvailable
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                selectedOptions {
                  name
                  value
                }
                image {
                  id
                  url
                  altText
                }
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = `
  query getProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      handle
      description
      descriptionHtml
      productType
      vendor
      createdAt
      updatedAt
      tags
      availableForSale
      totalInventory
      images(first: ${SHOPIFY_LIMITS.IMAGES_DETAIL_VIEW}) {
        edges {
          node {
            id
            url
            altText
          }
        }
      }
      variants(first: ${SHOPIFY_LIMITS.VARIANTS_PER_PRODUCT}) {
        edges {
          node {
            id
            title
            availableForSale
            quantityAvailable
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            selectedOptions {
              name
              value
            }
            image {
              id
              url
              altText
            }
          }
        }
      }
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      compareAtPriceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      options {
        name
        values
      }
    }
  }
`;

const COLLECTIONS_QUERY = `
  query getCollections($first: Int = ${SHOPIFY_LIMITS.COLLECTIONS_PER_PAGE}, $after: String) {
    collections(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          handle
          descriptionHtml
          image {
            id
            url
            altText
          }
          products(first: ${SHOPIFY_LIMITS.PRODUCTS_PER_COLLECTION}) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                handle
              }
            }
          }
        }
      }
    }
  }
`;

const COLLECTION_PRODUCTS_QUERY = `
  query getCollectionProducts($handle: String!, $first: Int = ${SHOPIFY_LIMITS.PRODUCTS_PER_COLLECTION}, $after: String) {
    collectionByHandle(handle: $handle) {
      id
      title
      handle
      products(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            title
            handle
            description
            descriptionHtml
            productType
            vendor
            createdAt
            updatedAt
            tags
            availableForSale
            totalInventory
            images(first: ${SHOPIFY_LIMITS.IMAGES_COLLECTION_VIEW}) {
              edges {
                node {
                  id
                  url
                  altText
                }
              }
            }
            variants(first: ${SHOPIFY_LIMITS.VARIANTS_PER_PRODUCT}) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  quantityAvailable
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  selectedOptions {
                    name
                    value
                  }
                  image {
                    id
                    url
                    altText
                  }
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  }
`;

// ============= Helper Functions =============

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));
const isRetriableStatus = (status: number) => status === 429 || status >= 500;

async function shopifyFetch<T>(
  query: string,
  variables: Record<string, any> = {},
  options: { retries?: number; retryDelayMs?: number } = {}
): Promise<T> {
  const { retries = 2, retryDelayMs = 600 } = options;

  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    attempt++;

    try {
      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        const text = await response.text();
        productionLogger.error('Shopify API Error Response:', text);
        if (isRetriableStatus(response.status) && attempt <= retries + 1) {
          const delay = retryDelayMs * Math.pow(2, attempt - 1) + Math.floor(Math.random() * 250);
          await sleep(delay);
          continue;
        }
        throw new Error(`Shopify HTTP ${response.status} ${response.statusText}: ${text.slice(0, 200)}`);
      }

      const json = await response.json();

      if (json.errors) {
        const message = json.errors[0]?.message || 'Unknown GraphQL error';
        if (attempt <= retries + 1) {
          const delay = retryDelayMs * Math.pow(2, attempt - 1) + Math.floor(Math.random() * 250);
          await sleep(delay);
          continue;
        }
        throw new Error(`Shopify GraphQL: ${message}`);
      }

      return json.data as T;
    } catch (err: any) {
      if (attempt <= retries + 1) {
        const delay = retryDelayMs * Math.pow(2, attempt - 1) + Math.floor(Math.random() * 250);
        await sleep(delay);
        continue;
      }
      productionLogger.error('Shopify Fetch Error (final):', err);
      throw err;
    }
  }
}

// ============= Public API Functions =============

/**
 * Fetch a specific page of products from a collection
 */
export async function fetchCollectionPage(
  collectionHandle: string,
  page: number = 1,
  perPage: number = 24
): Promise<{
  products: ShopifyProduct[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  currentPage: number;
}> {
  const first = perPage;
  const variables: any = { 
    handle: collectionHandle,
    first
  };

  const data = await shopifyFetch<{
    collectionByHandle: {
      products: {
        edges: Array<{ node: any }>;
        pageInfo: { hasNextPage: boolean };
      };
    };
  }>(COLLECTION_PRODUCTS_QUERY, variables);

  if (!data.collectionByHandle) {
    return {
      products: [],
      hasNextPage: false,
      hasPreviousPage: false,
      totalCount: 0,
      currentPage: page,
    };
  }

  const products: ShopifyProduct[] = data.collectionByHandle.products.edges
    .map(({ node }) => ({
      id: node.id,
      title: node.title,
      handle: node.handle,
      description: node.description || '',
      descriptionHtml: node.descriptionHtml,
      productType: node.productType || '',
      vendor: node.vendor || '',
      createdAt: node.createdAt,
      updatedAt: node.updatedAt,
      tags: node.tags || [],
      availableForSale: node.availableForSale,
      totalInventory: node.totalInventory || 0,
      images: node.images.edges.map(({ node: img }: any) => ({
        id: img.id,
        url: img.url,
        altText: img.altText,
      })),
      variants: node.variants.edges.map(({ node: variant }: any) => ({
        id: variant.id,
        title: variant.title,
        availableForSale: variant.availableForSale,
        quantityAvailable: variant.quantityAvailable || 0,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        selectedOptions: variant.selectedOptions || [],
        image: variant.image ? {
          id: variant.image.id,
          url: variant.image.url,
          altText: variant.image.altText,
        } : undefined,
      })),
      priceRange: node.priceRange,
      compareAtPriceRange: node.compareAtPriceRange,
    }));

  return {
    products,
    hasNextPage: data.collectionByHandle.products.pageInfo.hasNextPage,
    hasPreviousPage: page > 1,
    totalCount: products.length, // Approximation
    currentPage: page,
  };
}

/**
 * Fetch initial products batch for fast display (24 products)
 */
export async function fetchInitialProducts(): Promise<ShopifyProduct[]> {
  const variables = { 
    first: SHOPIFY_LIMITS.PRODUCTS_INITIAL_BATCH,
    sortKey: 'UPDATED_AT',
    reverse: true
  };
  
  const data = await shopifyFetch<{ 
    products: { 
      edges: Array<{ node: any }>, 
      pageInfo: { hasNextPage: boolean, endCursor: string } 
    } 
  }>(PRODUCTS_QUERY, variables);

  return data.products.edges
    .map(({ node }) => ({
      id: node.id,
      title: node.title,
      handle: node.handle,
      description: node.description || '',
      descriptionHtml: node.descriptionHtml,
      productType: node.productType || '',
      vendor: node.vendor || '',
      createdAt: node.createdAt,
      updatedAt: node.updatedAt,
      tags: node.tags || [],
      availableForSale: node.availableForSale,
      totalInventory: node.totalInventory || 0,
      images: node.images.edges.map(({ node: img }: any) => ({
        id: img.id,
        url: img.url,
        altText: img.altText,
      })),
      variants: node.variants.edges.map(({ node: variant }: any) => ({
        id: variant.id,
        title: variant.title,
        availableForSale: variant.availableForSale,
        quantityAvailable: variant.quantityAvailable || 0,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        selectedOptions: variant.selectedOptions || [],
        image: variant.image ? {
          id: variant.image.id,
          url: variant.image.url,
          altText: variant.image.altText,
        } : undefined,
      })),
      priceRange: node.priceRange,
      compareAtPriceRange: node.compareAtPriceRange,
    }))
    .filter(product => 
      // Keep products that have at least one variant with inventory
      product.variants.some(variant => variant.quantityAvailable > 0)
    );
}

/**
 * Fetch all products from Shopify with pagination
 */
export async function fetchAllProducts(): Promise<ShopifyProduct[]> {
  let allProducts: ShopifyProduct[] = [];
  let hasNextPage = true;
  let cursor: string | null = null;
  let pageCount = 0;

  while (hasNextPage && pageCount < SHOPIFY_LIMITS.MAX_PRODUCT_PAGES) {
    const variables: any = { 
      first: SHOPIFY_LIMITS.PRODUCTS_PER_PAGE,
      sortKey: 'UPDATED_AT',
      reverse: true
    };
    if (cursor) {
      variables.after = cursor;
    }

    const data = await shopifyFetch<{ 
      products: { 
        edges: Array<{ node: any }>, 
        pageInfo: { hasNextPage: boolean, endCursor: string } 
      } 
    }>(PRODUCTS_QUERY, variables);

    const pageProducts: ShopifyProduct[] = data.products.edges
      .map(({ node }) => ({
        id: node.id,
        title: node.title,
        handle: node.handle,
        description: node.description || '',
        descriptionHtml: node.descriptionHtml,
        productType: node.productType || '',
        vendor: node.vendor || '',
        createdAt: node.createdAt,
        updatedAt: node.updatedAt,
        tags: node.tags || [],
        availableForSale: node.availableForSale,
        totalInventory: node.totalInventory || 0,
        images: node.images.edges.map(({ node: img }: any) => ({
          id: img.id,
          url: img.url,
          altText: img.altText,
        })),
        variants: node.variants.edges.map(({ node: variant }: any) => ({
          id: variant.id,
          title: variant.title,
          availableForSale: variant.availableForSale,
          quantityAvailable: variant.quantityAvailable || 0,
          price: variant.price,
          compareAtPrice: variant.compareAtPrice,
          selectedOptions: variant.selectedOptions || [],
          image: variant.image ? {
            id: variant.image.id,
            url: variant.image.url,
            altText: variant.image.altText,
          } : undefined,
        })),
        priceRange: node.priceRange,
        compareAtPriceRange: node.compareAtPriceRange,
      }))
      .filter(product => 
        // Keep products that have at least one variant with inventory
        product.variants.some(variant => variant.quantityAvailable > 0)
      );

    allProducts = [...allProducts, ...pageProducts];
    hasNextPage = data.products.pageInfo.hasNextPage;
    cursor = data.products.pageInfo.endCursor;
    pageCount++;
  }

  return allProducts;
}

/**
 * Fetch all collections from Shopify with full product handle pagination
 */
export async function fetchAllCollections(): Promise<ShopifyCollection[]> {
  let allCollections: ShopifyCollection[] = [];
  let hasNextPage = true;
  let cursor: string | null = null;
  let pageCount = 0;

  while (hasNextPage && pageCount < SHOPIFY_LIMITS.MAX_COLLECTION_PAGES) {
    const variables: any = { first: SHOPIFY_LIMITS.COLLECTIONS_PER_PAGE };
    if (cursor) {
      variables.after = cursor;
    }

    const data = await shopifyFetch<{ 
      collections: { 
        edges: Array<{ node: any }>, 
        pageInfo: { hasNextPage: boolean, endCursor: string } 
      } 
    }>(COLLECTIONS_QUERY, variables);

    const pageCollections: ShopifyCollection[] = [];
    
    // Process collections sequentially to avoid rate limiting
    for (const { node } of data.collections.edges) {
      // Add delay between collections to prevent rate limiting
      if (pageCollections.length > 0) {
        await sleep(150);
      }
      
      // Fetch ALL products for this collection with proper pagination
      let allProductHandles: string[] = [];
      let hasNextProductPage = true;
      let productCursor: string | null = null;
      let productPageCount = 0;
      
      // Start with initial products from the collection query
      allProductHandles = node.products.edges.map(({ node: product }: any) => product.handle);
      hasNextProductPage = node.products.pageInfo.hasNextPage;
      productCursor = node.products.pageInfo.endCursor;
      
      // Fetch remaining products with proper pagination
      while (hasNextProductPage && productPageCount < SHOPIFY_LIMITS.MAX_COLLECTION_PRODUCT_PAGES) {
        try {
          productPageCount++;
          await sleep(100);
          
          const productVariables = {
            handle: node.handle,
            first: SHOPIFY_LIMITS.PRODUCTS_PAGINATION_CHUNK,
            after: productCursor
          };
          
          const productData = await shopifyFetch<{ 
            collectionByHandle: { 
              products: { 
                edges: Array<{ node: { handle: string } }>,
                pageInfo: { hasNextPage: boolean, endCursor: string }
              } 
            } 
          }>(`
            query getCollectionProducts($handle: String!, $first: Int!, $after: String) {
              collectionByHandle(handle: $handle) {
                products(first: $first, after: $after) {
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                  edges {
                    node {
                      handle
                    }
                  }
                }
              }
            }
          `, productVariables);
          
          const moreHandles = productData.collectionByHandle.products.edges.map(
            ({ node: product }) => product.handle
          );
          allProductHandles = [...allProductHandles, ...moreHandles];
          
          hasNextProductPage = productData.collectionByHandle.products.pageInfo.hasNextPage;
          productCursor = productData.collectionByHandle.products.pageInfo.endCursor;
        } catch (error) {
          productionLogger.error(`Error fetching page ${productPageCount} for collection ${node.handle}:`, error);
          break;
        }
      }
      
      pageCollections.push({
        id: node.id,
        title: node.title,
        handle: node.handle,
        description: node.descriptionHtml || '',
        image: node.image ? {
          id: node.image.id,
          url: node.image.url,
          altText: node.image.altText,
        } : undefined,
        productHandles: allProductHandles,
      });
    }

    allCollections = [...allCollections, ...pageCollections];
    hasNextPage = data.collections.pageInfo.hasNextPage;
    cursor = data.collections.pageInfo.endCursor;
    pageCount++;
  }

  return allCollections;
}

/**
 * Fetch a single product by handle
 */
export async function fetchProductByHandle(handle: string): Promise<ShopifyProduct | null> {
  try {
    const data = await shopifyFetch<{ productByHandle: any }>(
      PRODUCT_BY_HANDLE_QUERY,
      { handle }
    );

    if (!data.productByHandle) {
      return null;
    }

    const node = data.productByHandle;
    return {
      id: node.id,
      title: node.title,
      handle: node.handle,
      description: node.description || '',
      descriptionHtml: node.descriptionHtml,
      productType: node.productType || '',
      vendor: node.vendor || '',
      createdAt: node.createdAt,
      updatedAt: node.updatedAt,
      tags: node.tags || [],
      availableForSale: node.availableForSale,
      totalInventory: node.totalInventory || 0,
      images: node.images.edges.map(({ node: img }: any) => ({
        id: img.id,
        url: img.url,
        altText: img.altText,
      })),
      variants: node.variants.edges.map(({ node: variant }: any) => ({
        id: variant.id,
        title: variant.title,
        availableForSale: variant.availableForSale,
        quantityAvailable: variant.quantityAvailable || 0,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        selectedOptions: variant.selectedOptions || [],
        image: variant.image ? {
          id: variant.image.id,
          url: variant.image.url,
          altText: variant.image.altText,
        } : undefined,
      })),
      priceRange: node.priceRange,
      compareAtPriceRange: node.compareAtPriceRange,
      options: node.options || [],
    };
  } catch (error: any) {
    productionLogger.error('Error fetching product by handle:', error);
    throw error;
  }
}

/**
 * Fetch all products in a specific collection (with pagination)
 */
export async function fetchCollectionProducts(collectionHandle: string): Promise<ShopifyProduct[]> {
  try {
    let allProducts: ShopifyProduct[] = [];
    let hasNextPage = true;
    let cursor: string | null = null;
    let pageCount = 0;

    while (hasNextPage && pageCount < SHOPIFY_LIMITS.MAX_COLLECTION_PRODUCT_PAGES) {
      const variables: any = { handle: collectionHandle, first: SHOPIFY_LIMITS.PRODUCTS_PER_COLLECTION };
      if (cursor) {
        variables.after = cursor;
      }

      const data = await shopifyFetch<{ collectionByHandle: any }>(
        COLLECTION_PRODUCTS_QUERY,
        variables
      );

      if (!data.collectionByHandle) {
        return allProducts;
      }

      const formattedProducts: ShopifyProduct[] = data.collectionByHandle.products.edges
        .map(({ node }: any) => ({
          id: node.id,
          title: node.title,
          handle: node.handle,
          description: node.description || '',
          descriptionHtml: node.descriptionHtml,
          productType: node.productType || '',
          vendor: node.vendor || '',
          createdAt: node.createdAt,
          updatedAt: node.updatedAt,
          tags: node.tags || [],
          availableForSale: node.availableForSale,
          totalInventory: node.totalInventory || 0,
          images: node.images.edges.map(({ node: img }: any) => ({
            id: img.id,
            url: img.url,
            altText: img.altText,
          })),
          variants: node.variants.edges.map(({ node: variant }: any) => ({
            id: variant.id,
            title: variant.title,
            availableForSale: variant.availableForSale,
            quantityAvailable: variant.quantityAvailable || 0,
            price: variant.price,
            compareAtPrice: variant.compareAtPrice,
            selectedOptions: variant.selectedOptions || [],
            image: variant.image ? {
              id: variant.image.id,
              url: variant.image.url,
              altText: variant.image.altText,
            } : undefined,
          })),
          priceRange: node.priceRange,
          compareAtPriceRange: node.compareAtPriceRange,
        }));

      allProducts = allProducts.concat(formattedProducts);
      hasNextPage = data.collectionByHandle.products.pageInfo.hasNextPage;
      cursor = data.collectionByHandle.products.pageInfo.endCursor;
      pageCount++;
    }

    return allProducts;
  } catch (error: any) {
    productionLogger.error('Error fetching products by collection:', error);
    throw error;
  }
}

/**
 * Create a Shopify cart permalink
 */
export function createCartPermalink(items: Array<{ variantId: string; quantity: number }>): string {
  const cartItems = items
    .map(item => `${item.variantId.replace('gid://shopify/ProductVariant/', '')}:${item.quantity}`)
    .join(',');
  
  return `https://${SHOPIFY_STORE_DOMAIN}/cart/${cartItems}`;
}
